import { Alert, Box, Button, Link } from '@mm/components';
import * as Widgets from '@mm/constants/widgets';
import { useGoogleSheetsCRUDActions } from '@mm/state/actions/GoogleSheetsCRUDActions';
import { useMainMenuCRUDActions } from '@mm/state/actions/MainMenuCRUDActions';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import widgetCRUDActions, {
  useWidgetCRUDActions,
} from '@mm/state/actions/WidgetCRUDActions';
import { ButtonType } from '@mm/state/model/ButtonModel';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import { ChatbotChannel } from '@mm/state/model/ChatbotModel';
import WidgetModel, { WidgetTypes } from '@mm/state/model/WidgetModel';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import {
  selectCurrentUserIsAdmin,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import {
  activeFacebookPageIdSelector,
  selectWidgetLoaderOrder,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { colors } from '@mm/utils';
import customersLogo from 'assets/logo-customers-blue.svg';
import classnames from 'classnames';
import { Graph } from 'graphlib';
import { cloneDeep } from 'lodash';
import { get } from 'lodash/fp';
import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import { Draggable, DropResult } from 'react-beautiful-dnd';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useSelector } from 'react-redux';
import { CheckIcon } from '../../../../../common/component-icons/CheckIcon/CheckIcon';
import { AddWidgetInsert } from '../../../../../common/components/AddWidgetInsert/AddWidgetInsert';
import { MMdndDroppable } from '../../../../../common/components/MMdndDroppable/MMdndDroppable';
import { LAST_WIDGET_SORT_ORDER } from '../../../../../common/helpers/widget-helpers/constants';
import { PathGraphContext } from '../../../../../common/widgets/common/PathGraph/PathGraph';
import { validateSmsRelatedWidgets } from '../validateSmsRelatedWidgets';
import { VALID_START_BOT_WIDGET_TYPES } from './constants';
import { MockWidget } from './MockWidget/MockWidget';
import './StartBotsDialogueEditor.pcss';
import { WidgetAlerts, WidgetByType } from './WidgetByType/WidgetByType';

export type StartBotsEditorProps = {
  className?: string;
  specificityId?: number; // editor can be rendered from scheduler attributes with same dialogueId
  widgets: WidgetModel[];
  dialogueId: number;
  hideTopWidgetAdder?: boolean;
  isDisabled?: boolean;
  chatbotId: number;
  isPath?: boolean;
  widgetAdderInitialAllowedWidgets?: WidgetTypes[];
  onInvalidInitialWidget?: () => void;
  disableFirstWidgetDeleting?: boolean;
  disableFirstWidgetConditionalLogic?: boolean;
  forAds?: boolean;
  platforms?: PlatformType[];
  disabledButtonTypes?: ButtonType[];
  keyWordsHeader?: string;
  keyWordsHeaderOnClick?: () => void;
  restrictToChannels?: ChatbotChannel[];
  isSmsRelated?: boolean;
  smsOnly?: boolean;
  widgetsToExclude?: WidgetTypes[];
  showOnlyNameAttributes?: boolean;
  checkInitialAllowedWidgets?: boolean;
  forInstagram?: boolean;
  showCountByChannel?: boolean;
  showAttributesStartWarning?: boolean;
  firstWidgetNotAllowedWarning?: WidgetAlerts;
  usedInSlideout?: boolean;
};

const sortWidgets = (w1: WidgetModel, w2: WidgetModel) =>
  w1.sortOrder - w2.sortOrder;

export const getFilteredWidgets = (
  widgets: WidgetModel[],
  pathGraph: Graph | null | undefined,
  isWidgetActive?: (widget: WidgetModel) => boolean,
) => {
  const validWidgets = widgets.filter(widget =>
    VALID_START_BOT_WIDGET_TYPES.includes(widget.type),
  );

  const widgetsToRemove = widgets.filter(
    widget => !VALID_START_BOT_WIDGET_TYPES.includes(widget.type),
  );

  if (widgetsToRemove && widgetsToRemove.length) {
    widgetCRUDActions.batchDelete(widgetsToRemove.map<number>(get('id')));
  }

  let hasActivePath = false;

  if (pathGraph) {
    const filteredWidgets: WidgetModel[] = [];
    for (let i = 0; i < validWidgets.length; i += 1) {
      const validWidget = validWidgets[i];
      filteredWidgets.push(validWidget);

      if (isWidgetActive?.(validWidget)) {
        hasActivePath = true;
        break;
      }
    }

    return { filteredWidgets, hasActivePath };
  }

  return { filteredWidgets: validWidgets, hasActivePath };
};

export const StartBotsDialogueEditor = memo((props: StartBotsEditorProps) => {
  const {
    className,
    widgets: widgetsUpdatingWithoutActualChange,
    dialogueId,
    isDisabled,
    chatbotId,
    hideTopWidgetAdder,
    widgetAdderInitialAllowedWidgets,
    widgetsToExclude,
    disableFirstWidgetDeleting,
    disableFirstWidgetConditionalLogic,
    forAds,
    disabledButtonTypes,
    keyWordsHeader,
    keyWordsHeaderOnClick,
    restrictToChannels,
    platforms,
    isSmsRelated,
    smsOnly,
    isPath,
    onInvalidInitialWidget,
    showOnlyNameAttributes = false,
    checkInitialAllowedWidgets,
    specificityId,
    forInstagram,
    showCountByChannel,
    showAttributesStartWarning = false,
    firstWidgetNotAllowedWarning = {
      showWarning: false,
      message: '',
      startbotType: null,
    },
    usedInSlideout,
  } = props;

  const widgets = useMemo(() => {
    return widgetsUpdatingWithoutActualChange;
  }, [JSON.stringify(widgetsUpdatingWithoutActualChange)]);
  const page = useSelector((state: StoreState) =>
    pageEntityByIdSelector(state, dialogueId.toString()),
  );
  const isAdmin = useSelector(selectCurrentUserIsAdmin);

  const {
    pathGraph,
    removeNodeAndSuccessors,
    isWidgetActive,
    pathGraphLastUpdate,
    addDialogueWidgetsToGraph,
  } = useContext(PathGraphContext);
  const widgetCRUDActions = useWidgetCRUDActions();
  const pageCRUDActions = usePageCRUDActions();
  const mainMenuCRUDActions = useMainMenuCRUDActions();
  const { getAccounts } = useGoogleSheetsCRUDActions();
  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const menuItems = useSelector((state: StoreState) => state.mainMenu.entities);
  const [requestAccountsRequired, setRequestAccountRequired] = useState(true);
  const isGoogleSheets = widgets.find(w => w.config.kind === 'google_sheets');

  const { filteredWidgets, hasActivePath } = useMemo(() => {
    return getFilteredWidgets(widgets, pathGraph, isWidgetActive);
  }, [widgets, pathGraph, isWidgetActive, pathGraphLastUpdate]);

  const showSmsRulesWarning =
    isSmsRelated && !validateSmsRelatedWidgets(filteredWidgets);

  const session = useSelector(selectSessionSelector);

  useEffect(() => {
    if (
      !isPath &&
      menuItems.length === 0 &&
      chatbotId &&
      session &&
      !session.isGuest &&
      !usedInSlideout
    ) {
      mainMenuCRUDActions.retrieveAll(chatbotId);
    }
  }, [chatbotId]);

  useEffect(() => {
    if (
      checkInitialAllowedWidgets &&
      widgetAdderInitialAllowedWidgets &&
      onInvalidInitialWidget
    ) {
      const validInitialWidget =
        widgets.length === 0
          ? false
          : widgetAdderInitialAllowedWidgets.some(widget => {
              return widget === widgets[0].type;
            });
      if (!validInitialWidget) {
        onInvalidInitialWidget();
      }
    }
  }, [checkInitialAllowedWidgets, widgets.length]);

  const [isHovered, setIsHovered] = useState(false);
  const [updatingOrder, setUpdatingOrder] = useState(false);
  const [alertVisible, setAlertVisible] = useState(true);

  const hoverLast = (isLastChild: boolean) => {
    if (!filteredWidgets.length) {
      return true;
    }
    if (isLastChild) {
      return !isHovered;
    }
    return false;
  };

  const onDragEnd = async (
    result: DropResult,
    disableFirstItemDrop?: boolean,
  ) => {
    if (result.destination) {
      const { source, destination } = result;

      if (!destination || (disableFirstItemDrop && destination.index === 0)) {
        return;
      }

      setUpdatingOrder(true);

      const nextWidgets = cloneDeep(widgets.sort(sortWidgets));

      nextWidgets.splice(source.index, 1);
      nextWidgets.splice(destination.index, 0, widgets[source.index]);

      const updatedWidgets = nextWidgets.map((widget, index) => ({
        ...widget,
        sortOrder:
          widget.sortOrder === LAST_WIDGET_SORT_ORDER
            ? widget.sortOrder
            : index + 1,
      }));

      await widgetCRUDActions.batchUpdate(dialogueId, updatedWidgets, {
        sortOrdersOnly: true,
      });
      setUpdatingOrder(false);
    }
  };

  const [tokenCopied, setTokenCopied] = useState(false);
  const onImport = async () => {
    const searchToken = prompt(
      'Super Admin Feature: Enter source page token.  NOTE: This will overwrite all widgets in this dialogue.  THERE IS NO UNDO!',
    );
    if (searchToken) {
      const result = await pageCRUDActions.importFromSearchToken(
        dialogueId,
        searchToken,
      );
      if (!result) {
        alert('Invalid search token');
      }
    }
  };
  const widgetLoaderOrder = useSelector(selectWidgetLoaderOrder);

  useEffect(() => {
    if (widgets?.length) {
      addDialogueWidgetsToGraph?.(widgets);
    }

    return () => {
      removeNodeAndSuccessors?.({
        id: dialogueId,
        type: 'dialogue',
        actionType: 'deleteSuccessors',
      });
    };
  }, [dialogueId, pathGraph === null, widgets]);

  useEffect(() => {
    if (!isPath && isGoogleSheets && requestAccountsRequired) {
      getAccounts(facebookPageId);
      setRequestAccountRequired(false);
    }
  }, [isGoogleSheets]);

  let droppableId = `start-bots-widget-droppable-${dialogueId}`;
  if (specificityId) {
    droppableId += `${droppableId}-${specificityId}`;
  }

  const sortedWidgets = filteredWidgets.sort(sortWidgets);

  const [focusWidgetId, setFocusWidgetId] = useState<number | undefined>();

  return (
    <div
      className={classnames(className, 'start-bots-dialogue-editor', {
        'start-bots-dialogue-editor--disabled': isDisabled,
      })}
    >
      {isAdmin && (
        <Box className="start-bots-dialogue-editor__admin-tools">
          <img src={customersLogo} />
          <CopyToClipboard
            text={page?.searchToken}
            onCopy={() => setTokenCopied(true)}
          >
            <Button size="sm">
              Copy Token
              {tokenCopied && <CheckIcon size={12} color={colors.green} />}
            </Button>
          </CopyToClipboard>
          <Button size="sm" onClick={onImport}>
            Import
          </Button>
        </Box>
      )}
      {showSmsRulesWarning && alertVisible && (
        <Alert
          type="warning"
          className="start-bots-dialogue-editor__warning-alert"
          closeIconProps={{ onClick: () => setAlertVisible(false) }}
        >
          These messages might not look right when sent.{' '}
          <Link
            className="start-bots-dialogue-editor__warning-link"
            href="http://customers.ai/help/article/221sjia5jj-sending-long-messages-as-mms"
            isBlank
          >
            Learn how to send great Text Message here!
          </Link>
        </Alert>
      )}

      {keyWordsHeader && (
        <div className="start-bots-dialogue-editor__keyword">
          <div
            className="start-bots-dialogue-editor__keyword-text"
            onClick={keyWordsHeaderOnClick}
          >
            {keyWordsHeader}
          </div>
        </div>
      )}
      {!hideTopWidgetAdder && (
        <AddWidgetInsert
          sortOrder={0}
          dialogueId={dialogueId}
          allowedWidgets={widgetAdderInitialAllowedWidgets}
          widgetsToExclude={widgetsToExclude}
          isLastChild={hoverLast(!filteredWidgets.length)}
          setIsHovered={setIsHovered}
          isSmsRelated={isSmsRelated}
        />
      )}

      {widgetLoaderOrder === 0 && <MockWidget />}

      <MMdndDroppable
        droppableId={droppableId}
        type={`droppable-${dialogueId}`}
        onDragEnd={result => onDragEnd(result, disableFirstWidgetDeleting)}
      >
        {droppableProvided => (
          <div
            ref={droppableProvided.innerRef}
            className="start-bots-dialogue-editor__droppable"
          >
            {sortedWidgets.map((widget, index) => {
              return (
                <>
                  {widget.sortOrder === widgetLoaderOrder && <MockWidget />}
                  <Draggable
                    key={widget.id}
                    draggableId={`${widget.id}`}
                    index={index}
                    isDragDisabled={
                      updatingOrder ||
                      (disableFirstWidgetDeleting && index === 0)
                    }
                  >
                    {(draggableProvided, draggableStateSnapshot) => (
                      <div
                        className="start-bots-dialogue-editor__question-wrapper"
                        key={widget.id}
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                      >
                        <WidgetByType
                          forAds={forAds && index === 0}
                          forInstagram={forInstagram}
                          disabledButtonTypes={disabledButtonTypes}
                          widget={widget}
                          chatbotId={chatbotId}
                          disableDragging={
                            disableFirstWidgetDeleting && index === 0
                          }
                          disableDeleting={
                            disableFirstWidgetDeleting && index === 0
                          }
                          disableConditionalLogic={
                            disableFirstWidgetConditionalLogic && index === 0
                          }
                          restrictToChannels={restrictToChannels}
                          draggableProvided={draggableProvided}
                          isSmsRelated={isSmsRelated}
                          smsOnly={smsOnly}
                          platforms={platforms}
                          showAttributesStartWarning={
                            (showAttributesStartWarning &&
                              !(
                                firstWidgetNotAllowedWarning.showWarning &&
                                index === 0 &&
                                widgets.length > 1
                              )) ||
                            [
                              Widgets.QUICK_QUESTION,
                              WidgetTypes.FORM,
                              WidgetTypes.SMS_OPT_IN_V2,
                              WidgetTypes.SMS_OPT_IN_REPLY,
                            ].includes(widget.type)
                          }
                          firstWidgetNotAllowedWarning={{
                            showWarning:
                              firstWidgetNotAllowedWarning.showWarning &&
                              index === 0 &&
                              (widget.type === 'go_to' || widgets.length > 1),
                            message: firstWidgetNotAllowedWarning.message,
                            startbotType:
                              firstWidgetNotAllowedWarning.startbotType,
                          }}
                          showAttributePicker={
                            !showAttributesStartWarning || index !== 0
                          }
                          showOnlyNameAttributes={
                            showOnlyNameAttributes && index === 0
                          }
                          dialogueId={dialogueId}
                          onFocus={setFocusWidgetId}
                          isFocused={focusWidgetId === widget.id}
                          isDragging={draggableStateSnapshot.isDragging}
                          widgetsToExclude={widgetsToExclude}
                          showCountByChannel={showCountByChannel}
                          usedInSlideout={usedInSlideout}
                          pageWidgets={filteredWidgets}
                        />

                        {!hasActivePath &&
                          !draggableStateSnapshot.isDragging && (
                            <AddWidgetInsert
                              sortOrder={widget.sortOrder + 1}
                              dialogueId={dialogueId}
                              isLastChild={hoverLast(
                                filteredWidgets.length === index + 1,
                              )}
                              setIsHovered={setIsHovered}
                              isSmsRelated={isSmsRelated}
                              widgetsToExclude={widgetsToExclude}
                            />
                          )}
                        {hasActivePath &&
                          filteredWidgets.length !== index + 1 && (
                            <AddWidgetInsert
                              sortOrder={widget.sortOrder + 1}
                              dialogueId={dialogueId}
                              isLastChild={hoverLast(
                                filteredWidgets.length === index + 1,
                              )}
                              setIsHovered={setIsHovered}
                              isSmsRelated={isSmsRelated}
                              widgetsToExclude={widgetsToExclude}
                            />
                          )}
                      </div>
                    )}
                  </Draggable>
                  {widget.sortOrder + 1 === widgetLoaderOrder && <MockWidget />}
                </>
              );
            })}
          </div>
        )}
      </MMdndDroppable>
      {isDisabled && (
        <div className="start-bots-dialogue-editor__disabled-blinder" />
      )}
    </div>
  );
});
